import React from "react";
import {
  DateField,
  DropdownField,
  MultiSelectField,
  TextField,
} from "../../../components/ui";
import {
  normaliseEmailInput,
  normalisePhoneInput,
  toNameCase,
} from "../../../lib/fieldFormat";
import {
  ALL_CITIES,
  STATE_OPTIONS,
  citiesOfState,
} from "../../../lib/locations";
import { GENDER_CHOICES_LOCKED } from "../../shared/components/GenderMarks";
import {
  HEIGHT_UNITS,
  WEIGHT_UNITS,
  convertHeight,
  convertWeight,
} from "../preconsultationMeasures";
import { HABIT_OPTIONS, MAX_DISHES } from "../preconsultationSteps";
import {
  EARLIEST_DOB,
  ageFromDob,
  digitsOnly,
  normaliseDobInput,
  normaliseNameInput,
} from "../preconsultationValidation";
import type {
  PreconsultationErrors,
  PreconsultationField,
} from "../preconsultationValidation";
import type {
  PreconsultationAnswers,
  PreconsultationCatalogs,
  PreconsultationFields,
} from "../types/preconsultation.types";
import {
  Card,
  ChipsField,
  ChoiceField,
  MeasureField,
  NoMark,
  TimeRangeField,
  YesMark,
} from "./PreconsultationControls";

/**
 * The six screens of questions, one component each.
 *
 * They share a props shape and hold no state: every answer lives in the page
 * above them, because the review screen reads all forty at once and a step
 * that kept its own would lose them the moment it was stepped past. What is
 * here is layout and nothing else.
 *
 * Cards are what make a screenful of fields read as two or three questions
 * rather than a wall — the same trick the enquiry form uses, and the grouping
 * is the design's.
 */

export interface StepProps {
  answers: PreconsultationAnswers;
  /** Patches the answers above. Named `set` at every call site for brevity —
   *  these files are mostly markup and the noise would drown the layout. */
  set: (patch: Partial<PreconsultationAnswers>) => void;
  /** Only the problems worth showing yet — see `errorFor` on the page. */
  errors: PreconsultationErrors;
  /** Marks a field visited, so its rule can start being shown. */
  visit: (field: PreconsultationField) => () => void;
  catalogs: PreconsultationCatalogs;
  /**
   * Which questions this practitioner asks — see `PreconsultationFields`.
   *
   * A whole step nobody asks for never gets here at all: `stepsAsked` leaves
   * it out of the run. What this decides is the questions *inside* a step that
   * is being shown, where one part is asked and another is not — a form that
   * wants somebody's working hours but not their profession.
   */
  fields: PreconsultationFields;
}

/** Where a field's control can be found, for the scroll to the first problem. */
export const controlId = (field: PreconsultationField) => `pc-${field}`;

/* ── 1. Basics ────────────────────────────────────────────────────────── */

/**
 * Everything on this step except the two open fields.
 *
 * The step is what the practitioner already holds, shown back to be checked —
 * see `PreconsultationBasics`, which is the face it opens on. These are the
 * rows behind Edit that are theirs rather than the client's: a name, a number
 * and an email the workspace was given when the client was added, and a goal
 * they were signed up against.
 *
 * Drawn rather than left out, because the point of the screen is that the
 * client can see what is held about them. A field removed says nothing; a
 * field they cannot type into says "we have this already".
 */
const LOCKED = true;

export const BasicsStep: React.FC<StepProps> = ({
  answers,
  set,
  errors,
  visit,
  catalogs,
}) => (
  <>
    <Card>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          id={controlId("firstName")}
          label="First name"
          placeholder="eg Riya"
          value={answers.firstName}
          onValueChange={(value) =>
            set({ firstName: toNameCase(normaliseNameInput(value)) })
          }
          onBlur={visit("firstName")}
          error={errors.firstName}
          autoComplete="given-name"
          disabled={LOCKED}
        />
        <TextField
          id={controlId("lastName")}
          label="Last name"
          placeholder="Roy"
          value={answers.lastName}
          onValueChange={(value) =>
            set({ lastName: toNameCase(normaliseNameInput(value)) })
          }
          onBlur={visit("lastName")}
          error={errors.lastName}
          autoComplete="family-name"
          disabled={LOCKED}
        />
      </div>

      {/*
        No age box, on purpose and not by omission. An age and a date of birth
        are one answer asked twice, and asking both invites the two to
        disagree — a record saying 26 beside a birthday saying 31, with nothing
        to say which the practitioner should plan from. The birthday is the one
        that stays true, so it is the one that is asked; the age is counted off
        it and read back on the card, and where there is no birthday the age
        the record already held is what stands.
      */}
      {/* The date and the pair share a row from `sm:` up, under the two names
          above them — four fields on two lines rather than three lines with a
          gap down the right of one. Stacked on a phone, where the pair needs
          the whole width to stay a target worth aiming at. */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* One of the two fields on this step that are the client's own. It is
            the thing whoever added them usually did not have, and the whole
            reason the step is worth opening at all. */}
        <DateField
          id={controlId("dob")}
          label="DOB"
          value={answers.dob}
          normalise={normaliseDobInput}
          // The two rules `validateStep` checks for the same field, so the
          // calendar cannot offer a day the box would then complain about.
          minDate={EARLIEST_DOB}
          maxDate={new Date()}
          onValueChange={(dob) => {
            // Their age follows from it rather than being asked twice — see
            // `ageFromDob`. A half-typed date counts to nothing and leaves
            // whatever age the record already held alone, so the answer does
            // not blink out between the first digit and the last.
            const years = ageFromDob(dob);
            set(years ? { dob, age: years } : { dob });
          }}
          onBlur={visit("dob")}
          error={errors.dob}
        />
        <ChoiceField
          id={controlId("gender")}
          label="I am"
          value={answers.gender}
          // The muted marks, because the pair is locked — see
          // `GENDER_CHOICES_LOCKED`. A teal glyph on a flat grey button is the
          // one thing left on the row still claiming to be pressable.
          options={GENDER_CHOICES_LOCKED}
          onChange={(gender) => {
            set({ gender });
            visit("gender")();
          }}
          error={errors.gender}
          disabled={LOCKED}
        />
      </div>
    </Card>

    <Card>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Several at once, and all of them shown: a record signed up against
            weight and strength is on one plan, and a control taking a single
            answer would read back half of what it holds. The client's to see
            rather than to change — a goal is what their practitioner enrolled
            them against. */}
        <MultiSelectField
          id={controlId("goals")}
          label="Goal"
          placeholder="Weight loss"
          values={answers.goals}
          options={catalogs.goals.names}
          onValuesChange={(goals) => set({ goals })}
          onBlur={visit("goals")}
          error={errors.goals}
          disabled={LOCKED}
        />
        {/* The other open field. A goal is what the client was signed up
            against and is the practitioner's to change; how active their days
            actually are is theirs. */}
        <DropdownField
          id={controlId("lifestyle")}
          label="Life style"
          placeholder="Semi active"
          value={answers.lifestyle}
          options={catalogs.lifeStyles.names}
          onValueChange={(lifestyle) => set({ lifestyle })}
          onBlur={visit("lifestyle")}
          error={errors.lifestyle}
        />
      </div>
    </Card>

    <Card>
      {/* Two columns for two fields, not three. A third column with nothing in
          it left the pair stopping two thirds of the way across the card on a
          desktop, with the row above them running the full width — which reads
          as a field that failed to load rather than as a row of two. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          id={controlId("email")}
          label="Email address"
          placeholder="yourname@gmail.com"
          value={answers.email}
          onValueChange={(email) => set({ email: normaliseEmailInput(email) })}
          onBlur={visit("email")}
          error={errors.email}
          type="email"
          autoComplete="email"
          disabled={LOCKED}
        />
        <TextField
          id={controlId("phone")}
          label="Phone number"
          placeholder="7869 369 854"
          value={answers.phone}
          onValueChange={(phone) => set({ phone: normalisePhoneInput(phone) })}
          onBlur={visit("phone")}
          error={errors.phone}
          inputMode="numeric"
          autoComplete="tel-national"
          prefix="+91"
          disabled={LOCKED}
        />
      </div>
    </Card>
  </>
);

/* ── 2. Location ──────────────────────────────────────────────────────── */

export const LocationStep: React.FC<StepProps> = ({
  answers,
  set,
  errors,
  visit,
}) => (
  <Card>
    <TextField
      id={controlId("address")}
      label="Full address"
      placeholder="1202, Galaxy apartment, SV road"
      value={answers.address}
      onValueChange={(address) => set({ address })}
      onBlur={visit("address")}
      error={errors.address}
      autoComplete="street-address"
    />

    {/* The three that make up a place, on one line from `sm:` up — they are a
        state, a city and six digits, none of which wants the width of the card
        the address above them needs, and the design sets them as one row.
        Stacked on a phone, where a dropdown squeezed to a third of the screen
        cannot show the name of what is in it. */}
    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
      <DropdownField
        id={controlId("state")}
        label="State"
        placeholder="eg. Mumbai"
        value={answers.state}
        options={STATE_OPTIONS}
        // The city goes with the state: a city picked under Maharashtra is not
        // an answer about Kerala, and leaving it behind is how a wrong pair is
        // sent without anybody noticing.
        onValueChange={(state) => set({ state, locationCity: "" })}
        onBlur={visit("state")}
        error={errors.state}
        searchable
      />

      <DropdownField
        id={controlId("locationCity")}
        label="City"
        placeholder="eg. Mumbai"
        value={answers.locationCity}
        // Narrowed to the state above, so the pair cannot disagree. The whole
        // list before a state is picked, because somebody who knows their city
        // and not which state it is filed under should still be able to answer.
        options={
          answers.state ? citiesOfState(answers.state) : ALL_CITIES
        }
        onValueChange={(locationCity) => set({ locationCity })}
        onBlur={visit("locationCity")}
        error={errors.locationCity}
        searchable
        creatable
      />

      <TextField
        id={controlId("pincode")}
        label="Pincode"
        placeholder="eg. 410236"
        value={answers.pincode}
        onValueChange={(value) => set({ pincode: digitsOnly(value).slice(0, 6) })}
        onBlur={visit("pincode")}
        error={errors.pincode}
        inputMode="numeric"
        autoComplete="postal-code"
      />
    </div>
  </Card>
);

/* ── 3. Body ──────────────────────────────────────────────────────────── */

/**
 * The one step whose controls do more than hand what was typed straight up.
 *
 * Changing the unit beside a filled box converts the figure in it rather than
 * relabelling it — see `preconsultationMeasures`. Empty boxes are left alone,
 * so the unit can be picked before the number.
 */
export const BodyStep: React.FC<StepProps> = ({ answers, set, errors }) => (
  <>
    <Card>
      <MeasureField
        id={controlId("height")}
        label="Height"
        placeholder="eg. 168"
        value={answers.height}
        onValueChange={(height) => set({ height })}
        unit={answers.heightUnit}
        units={HEIGHT_UNITS}
        onUnitChange={(heightUnit) =>
          set({
            heightUnit,
            height: convertHeight(answers.height, answers.heightUnit, heightUnit),
          })
        }
        error={errors.height}
      />
    </Card>

    <Card>
      <MeasureField
        id={controlId("weight")}
        label="Weight"
        placeholder="eg. 68"
        value={answers.weight}
        onValueChange={(weight) => set({ weight })}
        unit={answers.weightUnit}
        units={WEIGHT_UNITS}
        onUnitChange={(weightUnit) =>
          // The target follows the weight's unit unless it has been set apart:
          // nobody weighs themselves in kilos and sets a goal in pounds, and
          // two units on one card is a misreading waiting to happen. Where it
          // does follow there is nothing in it to convert — that is the test
          // for having been set apart.
          set({
            weightUnit,
            weight: convertWeight(answers.weight, answers.weightUnit, weightUnit),
            targetWeightUnit: answers.targetWeight
              ? answers.targetWeightUnit
              : weightUnit,
          })
        }
        error={errors.weight}
      />

      <div className="mt-4">
        <MeasureField
          id={controlId("targetWeight")}
          label="Target weight"
          placeholder="eg. 58"
          value={answers.targetWeight}
          onValueChange={(targetWeight) => set({ targetWeight })}
          unit={answers.targetWeightUnit}
          units={WEIGHT_UNITS}
          onUnitChange={(targetWeightUnit) =>
            set({
              targetWeightUnit,
              targetWeight: convertWeight(
                answers.targetWeight,
                answers.targetWeightUnit,
                targetWeightUnit
              ),
            })
          }
          error={errors.targetWeight}
        />
      </div>
    </Card>
  </>
);

/* ── 4. Food ──────────────────────────────────────────────────────────── */

export const FoodStep: React.FC<StepProps> = ({
  answers,
  set,
  errors,
  visit,
  catalogs,
  fields,
}) => (
  <>
    {/* The two closed choices, and nothing else on the card — the design puts
        what somebody eats above what they cook and eat, and the card is left
        off entirely where this practitioner asks neither. Side by side from
        `sm:` up, the way the two pairs on the basics step are laid out: two
        one-line answers stacked full-width on a 1024px column is a lot of
        travel for one card, and neither needs the measure. */}
    {fields.foodPreference && (
      <Card>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DropdownField
            id={controlId("foodPreference")}
            label="Food preference"
            placeholder="Vegetarian"
            value={answers.foodPreference}
            options={catalogs.foodPreferences.names}
            onValueChange={(foodPreference) => set({ foodPreference })}
            onBlur={visit("foodPreference")}
            error={errors.foodPreference}
          />

          {/* Several, as the label has always said. Somebody who eats North
              Indian on weekdays and South Indian at the weekend was being
              asked to pick one of them, and a plan written off half an answer
              is the wrong plan. */}
          <MultiSelectField
            id={controlId("cuisines")}
            label="Favorite cuisines"
            placeholder="North Indian"
            values={answers.cuisines}
            options={catalogs.cuisines.names}
            onValuesChange={(cuisines) => set({ cuisines })}
            onBlur={visit("cuisines")}
            error={errors.cuisines}
          />
        </div>
      </Card>
    )}

    {/* The three lists: the dishes either way where they are asked, and the
        allergies, which are always. The card is never left off for that
        reason — an allergy has no flag to switch it off, and it is the one
        answer here a plan can harm somebody by not having. */}
    <Card>
      {fields.favoriteDishes && (
        <ChipsField
          id={controlId("dishesLoved")}
          label="Dishes you love"
          placeholder="eg. Rajma chawal"
          values={answers.dishesLoved}
          onChange={(dishesLoved) => set({ dishesLoved })}
          max={MAX_DISHES}
          note={`(Add upto ${MAX_DISHES})`}
        />
      )}

      {fields.foodsToAvoid && (
        <div className={fields.favoriteDishes ? "mt-4" : ""}>
          <ChipsField
            id={controlId("dishesAvoided")}
            label="Dishes you avoid"
            placeholder="eg. Bitter gourd sabzi"
            values={answers.dishesAvoided}
            onChange={(dishesAvoided) => set({ dishesAvoided })}
            max={MAX_DISHES}
            note={`(Add upto ${MAX_DISHES})`}
          />
        </div>
      )}

      {/* Last on the card and asked of everybody. Several, because people have
          several, and closed — an allergy goes up as the id of a row the
          backend knows, so one typed in has nothing to be sent as. */}
      <MultiSelectField
        id={controlId("foodAllergies")}
        label="Food allergies"
        placeholder="Peanut Allergy"
        values={answers.foodAllergies}
        options={catalogs.foodAllergies.names}
        onValuesChange={(foodAllergies) => set({ foodAllergies })}
        onBlur={visit("foodAllergies")}
        error={errors.foodAllergies}
        // Nothing above it where neither dish list is asked, and this is then
        // the whole card.
        className={
          fields.favoriteDishes || fields.foodsToAvoid ? "mt-4" : ""
        }
      />
    </Card>
  </>
);

/* ── 5. Your day ──────────────────────────────────────────────────────── */

export const DayStep: React.FC<StepProps> = ({
  answers,
  set,
  errors,
  visit,
  catalogs,
  fields,
}) => (
  <>
    {(fields.profession || fields.dailyRoutine) && (
      <Card>
        {fields.profession && (
          <DropdownField
            id={controlId("profession")}
            label="Profession"
            placeholder="Software engineer"
            value={answers.profession}
            options={catalogs.professions.names}
            onValueChange={(profession) => set({ profession })}
            onBlur={visit("profession")}
            error={errors.profession}
            searchable
            creatable
          />
        )}

        {fields.dailyRoutine && (
          <div className={fields.profession ? "mt-4" : ""}>
            <TimeRangeField
              id={controlId("workFrom")}
              label="Working hours"
              from={answers.workFrom}
              to={answers.workTo}
              onFromChange={(workFrom) => set({ workFrom })}
              onToChange={(workTo) => set({ workTo })}
              anyHours={answers.workAnyHours}
              onAnyHoursChange={(workAnyHours) => set({ workAnyHours })}
            />
          </div>
        )}
      </Card>
    )}

    {fields.dailyRoutine && (
      <Card>
        <TimeRangeField
          id={controlId("sleepFrom")}
          label="Sleep timings"
          from={answers.sleepFrom}
          to={answers.sleepTo}
          onFromChange={(sleepFrom) => set({ sleepFrom })}
          onToChange={(sleepTo) => set({ sleepTo })}
          anyHours={answers.sleepAnyHours}
          onAnyHoursChange={(sleepAnyHours) => set({ sleepAnyHours })}
        />
      </Card>
    )}

    {fields.lifestyleHabits && (
    <Card>
      <DropdownField
        id={controlId("travel")}
        label="Travel frequency"
        placeholder="Occasional travel"
        value={answers.travel}
        options={[...HABIT_OPTIONS.travel]}
        onValueChange={(travel) => set({ travel })}
        onBlur={visit("travel")}
        error={errors.travel}
      />

      <div className="mt-4">
        <DropdownField
          id={controlId("alcohol")}
          label="Alcohol consumption"
          placeholder="Occasionally"
          value={answers.alcohol}
          options={[...HABIT_OPTIONS.alcohol]}
          onValueChange={(alcohol) => set({ alcohol })}
          onBlur={visit("alcohol")}
          error={errors.alcohol}
        />
      </div>

      <div className="mt-4">
        <DropdownField
          id={controlId("smoking")}
          label="Smoking"
          placeholder="Occasionally"
          value={answers.smoking}
          options={[...HABIT_OPTIONS.smoking]}
          onValueChange={(smoking) => set({ smoking })}
          onBlur={visit("smoking")}
          error={errors.smoking}
        />
      </div>
    </Card>
    )}
  </>
);

/* ── 6. Health ────────────────────────────────────────────────────────── */

const MEDICATION_CHOICES = [
  { value: "No" as const, icon: NoMark },
  { value: "Yes" as const, icon: YesMark },
];

export const HealthStep: React.FC<StepProps> = ({
  answers,
  set,
  errors,
  visit,
  catalogs,
  fields,
}) => (
  <Card>
    {/* Several, and picked rather than typed. Somebody with both diabetes and
        a thyroid condition was being asked to pick the one that mattered
        most, and a plan written off half a health history is the wrong plan.
        Closed for the same reason the allergies are: each goes up as a
        catalog id, so a condition typed in has nothing to be sent as. */}
    {fields.medicalConditions && (
      <MultiSelectField
        id={controlId("conditions")}
        label="Medical conditions"
        placeholder="High cholesterol"
        values={answers.conditions}
        options={catalogs.medicalConditions.names}
        onValuesChange={(conditions) => set({ conditions })}
        onBlur={visit("conditions")}
        error={errors.conditions}
      />
    )}

    {fields.medications && (
    <div className={fields.medicalConditions ? "mt-4" : ""}>
      <ChoiceField
        id={controlId("onMedication")}
        label="On any medications"
        value={answers.onMedication}
        options={MEDICATION_CHOICES}
        onChange={(onMedication) =>
          // "No" takes the list with it. A row left behind under a No is a
          // contradiction the practitioner would have to resolve by guessing.
          set({
            onMedication,
            medications: onMedication === "No" ? [] : answers.medications,
          })
        }
        error={errors.onMedication}
        // Half the row from `sm:` up. Yes and No are two short words and do
        // not need 550px each to say so — stretched to the card they were the
        // biggest thing on a desktop screen, and the question above them is
        // the one that matters. The phone keeps them full width, where half a
        // 360px screen is a target somebody has to aim at.
        width="half"
      />
    </div>
    )}

    {/* Only once they have said there are some. Asking for the names of
        medications nobody takes is a question with no answer, and on a phone
        it is a screenful of one. */}
    {fields.medications && answers.onMedication === "Yes" && (
      <div className="mt-4">
        <ChipsField
          id={controlId("medications")}
          label="Name and dose if you know it."
          placeholder="Metformin 500mg"
          values={answers.medications}
          onChange={(medications) => set({ medications })}
          max={10}
        />
      </div>
    )}
  </Card>
);
